import { useRef } from 'react';
import { DateTime as DT } from 'luxon';
import type { ServiceType } from '@econtractor/types';
import { useAuth } from 'context/AuthContext';
import { useSettings } from 'context/SettingsContext';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import { formatPrice } from 'utils/helpers/functions/formatPrice';
import {
    withBaseCurrency,
    withLocalCurrency,
    withPrimaryCurrency,
} from 'utils/helpers/currency/currencyHelpers';
import CopyTextWrapper from 'components/others/CopyTextWrapper';
import ServiceTableRowActions from './ServiceTableRowActions';
import { useService } from './context/ServiceProvider';

interface ServiceTableRowProps {
    service: ServiceType;
    fetchServices: (force?: boolean) => Promise<unknown>;
}

const ServiceTableRow = ({ service, fetchServices }: ServiceTableRowProps) => {
    const { auth } = useAuth();
    const { localSettings } = useSettings();
    const rate = Number(localSettings?.RATE?.current ?? 1) || 1;
    const isUahMode = rate === 1;
    const price = formatPrice(service?.servDefPrice);
    const priceSale = formatPrice(service?.servDefPriceSale);
    const priceUah = formatPrice((service?.servDefPrice || 0) * rate);
    const priceSaleUah = formatPrice((service?.servDefPriceSale || 0) * rate);
    const dt = DT.fromMillis(service?.servUpdated || 0).toFormat('dd.MM.yyyy');
    const { setShowEditService, serviceCheck, setServiceCheck } = useService();
    const refCheck = useRef<HTMLInputElement>(null);

    return (
        <tr>
            <td className="check">
                <input
                    className="form-check-input"
                    disabled={!hasUserAccess(auth?.user, 'prodEdit')}
                    ref={refCheck}
                    id={`check-service-${service.ID}`}
                    checked={serviceCheck?.includes(service.ID)}
                    onChange={(e) => {
                        setServiceCheck((prev) => {
                            if (e.target.checked) {
                                return [...prev, service.ID];
                            }
                            return prev.filter((sId) => sId !== service.ID);
                        });
                    }}
                    type="checkbox"
                />
            </td>
            <td className="name">
                <h6
                    className="mb-1 text-break cursor-pointer"
                    onClick={() => setShowEditService(service.ID)}
                >
                    {service.servName || '-'}
                </h6>
                <p className="text-muted f-12 mb-0 my-1 text-truncate" title={service.servDescription || ''}>
                    {service.servDescription || '-'}
                </p>
            </td>
            <td className="id">
                <code>{service.ID}</code>
            </td>
            <td className="article text-truncate">
                <CopyTextWrapper value={service.servArticle || ''}>
                    <span title={service.servArticle}>{service.servArticle}</span>
                </CopyTextWrapper>
            </td>
            <td className="price text-truncate">
                {isUahMode ? (
                    <h6 className="mb-1">{withLocalCurrency(price)}</h6>
                ) : (
                    <h6 className="mb-1" title={withLocalCurrency(priceUah)}>
                        {withBaseCurrency(price)}
                    </h6>
                )}
                <p
                    className="f-12 mb-0 text-primary text-truncate"
                    title={isUahMode ? undefined : withLocalCurrency(priceSaleUah)}
                >
                    {withPrimaryCurrency(priceSale, rate)}
                </p>
            </td>
            <td className="updated">{dt}</td>
            {hasUserAccess(auth?.user, 'prodEdit') && (
                <td className="action">
                    <ServiceTableRowActions service={service} fetchServices={fetchServices} />
                </td>
            )}
        </tr>
    );
};

export default ServiceTableRow;
